import React, { createContext, useState, useEffect, useContext } from 'react';
import authService from '../services/authService';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showShopSelection, setShowShopSelection] = useState(false);

  useEffect(() => {
    const storedUser = authService.getCurrentUser();
    if (storedUser) {
      if (storedUser.shopId === null || storedUser.shopId === undefined) {
        setShowShopSelection(true);
      }
      setUser(storedUser);
    }

    const verifyAuth = async () => {
      try {
        setLoading(true);
        const response = await authService.checkAuth();
        if (response.success) {
          const currentUser = authService.getCurrentUser();
          setUser(currentUser);

          if (currentUser && (currentUser.shopId === null || currentUser.shopId === undefined)) {
            setShowShopSelection(true);
          }
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('Error al verificar autenticación:', err);
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    verifyAuth();
  }, []);

  const login = async (credentials) => {
    try {
      setLoading(true);
      setError(null);
      const response = await authService.login(credentials);
      if (response.success) {
        setUser(response.user);

        if (response.requiresShopSelection) {
          setShowShopSelection(true);
          return { success: true, requiresShopSelection: true };
        }

        return { success: true };
      } else {
        setError(response.message || 'Error al iniciar sesión');
        return { success: false, message: response.message };
      }
    } catch (err) {
      const errorMessage = err.message || 'Error al iniciar sesión';
      setError(errorMessage);
      return { success: false, message: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    try {
      setLoading(true);
      await authService.logout();
      setUser(null);
      setShowShopSelection(false);
    } catch (err) {
      console.error('Error al cerrar sesión:', err);
    } finally {
      setLoading(false);
    }
  };

  const isAdmin = () => {
    return user && user.isAdmin === 1;
  };

  const isManager = () => {
    return user && user.profileId === 3;
  };

  const isUser = () => {
    return user && user.profileId === 7;
  };

  const handleShopSelected = (updatedUser) => {
    setUser(updatedUser);
    setShowShopSelection(false);
  };

  const handleShopSelectionCancel = async () => {
    await logout();
    setShowShopSelection(false);
  };

  const isAuthenticated = () => {
    return !!user && (user.shopId !== null && user.shopId !== undefined);
  };

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAdmin,
    isManager,
    isUser,
    isAuthenticated,
    showShopSelection,
    handleShopSelected,
    handleShopSelectionCancel,
    roles: {
      isAdmin: user?.isAdmin === 1,
      isManager: user?.profileId === 3,
      isUser: user?.profileId === 7,
      profileId: user?.profileId,
      shopId: user?.shopId
    }
  };

  // console.log('=== DEBUG AuthContext value ===');
  // console.log('User en value:', user);
  // console.log('Roles en value:', value.roles);


  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth debe ser usado dentro de un AuthProvider');
  }
  return context;
};

export default AuthContext;